import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useStableKeyboardOffset } from "@/hooks/useStableKeyboardOffset";
import { useKeyboardOffset } from "@/hooks/useKeyboardOffset";
import {
  findNextField,
  goToNextField,
  hasPinnedKeyboardFooter,
  isNoReturnKeyField,
  wantsEnterNext,
} from "@/lib/keyboardNext";

/**
 * Next / Done strip on top of the number pad, app-wide. Mounted once in App.
 * Why it exists and why #331's hidden iOS bar stays hidden: lib/keyboardNext.ts.
 *
 * KEYBOARD ZONE: this only READS useStableKeyboardOffset, the same way
 * BottomSheet does. It never writes --vv-height, offset state or diagnostics.
 *
 * - Shown only while a no-Return-key field is focused AND the keyboard is up
 *   (offset > 80, the threshold sheetKeyboardGeometry uses: below that the
 *   value is the focus sentinel, not a keyboard). So: nothing on desktop,
 *   nothing over the chat box, nothing over a text keyboard.
 * - POSITION uses the RAW offset when it reads as a keyboard, falling back to
 *   the stable one only during iOS's between-field blip (raw 0). The stable
 *   offset is frozen once open, so hopping from a text field (taller, with
 *   QuickType) to the number pad would leave the strip floating above the
 *   shorter keys (Copilot, #2027). VISIBILITY still uses the stable offset, so
 *   the blip does not flicker the strip off. Neither value is written back;
 *   sheet geometry keeps reading the frozen one.
 * - Steps aside where the screen pins its own button on the keyboard
 *   (hasPinnedKeyboardFooter — the Party Planner wizard).
 * - 44px tall, under BottomSheet's RESCUE_MARGIN_PX (56): a field the sheet
 *   rescues into view is never hidden behind the strip.
 * - The strip preventDefaults pointer/mouse/touch down so the field keeps
 *   focus and the keyboard stays up through the tap. Without that, the tap
 *   blurs the field, the keyboard starts closing, the strip unmounts, and Next
 *   does nothing. It also STOPS PROPAGATION: the strip is portalled to body,
 *   so to a dropdown's outside-tap handler (KrogerStorePicker's zip editor)
 *   a tap here looks like a tap outside, and it would close the editor before
 *   Next/Done ran (Copilot, #2027).
 *
 * Also owns Return-to-advance for text inputs inside `[data-enter-next]`.
 * Runs on document, after React's own handlers, and stands aside if a field's
 * handler already used the key (defaultPrevented).
 */
export const KEYBOARD_NEXT_BAR_HEIGHT = 44;

const keepFocusNative = (e: Event) => {
  e.preventDefault();
  e.stopPropagation();
};
const stopOnly = (e: Event) => e.stopPropagation();

const KeyboardNextBar = () => {
  const offset = useStableKeyboardOffset();
  const raw = useKeyboardOffset();
  const barRef = useRef<HTMLDivElement | null>(null);
  const [field, setField] = useState<HTMLInputElement | null>(null);

  useEffect(() => {
    const onFocusIn = (e: FocusEvent) => {
      setField(isNoReturnKeyField(e.target) ? e.target : null);
    };
    const onFocusOut = () => {
      // Focus moving field-to-field fires focusout then focusin; read where it
      // landed on the next tick instead of flickering the strip off and on.
      window.setTimeout(() => {
        const active = document.activeElement;
        setField(isNoReturnKeyField(active) ? active : null);
      }, 0);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || e.shiftKey || e.isComposing) return;
      if (e.defaultPrevented) return;
      if (!wantsEnterNext(e.target)) return;
      e.preventDefault();
      goToNextField(e.target);
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  // NATIVE listeners on the strip element itself, not React props: the
  // dropdowns that close on outside taps listen on `document` natively, and a
  // React stopPropagation from a body portal is not guaranteed to stop those.
  // Stopping it here, at the target, always does.
  //
  // ⚠️ preventDefault on pointerdown/mousedown ONLY (keeps focus, so the
  // keyboard stays up). NEVER on touchstart: on iOS that cancels the tap's
  // click, and Next/Done would do nothing. touchstart only stops propagation.
  const attachBar = (el: HTMLDivElement | null) => {
    const prev = barRef.current;
    if (prev === el) return;
    if (prev) {
      prev.removeEventListener("pointerdown", keepFocusNative);
      prev.removeEventListener("mousedown", keepFocusNative);
      prev.removeEventListener("touchstart", stopOnly);
    }
    barRef.current = el;
    if (el) {
      el.addEventListener("pointerdown", keepFocusNative);
      el.addEventListener("mousedown", keepFocusNative);
      el.addEventListener("touchstart", stopOnly, { passive: true });
    }
  };

  if (!field || offset <= 80 || !field.isConnected) return null;
  if (hasPinnedKeyboardFooter(field)) return null;

  const bottom = raw > 80 ? raw : offset;
  const hasNext = !!findNextField(field);

  const btn = {
    height: KEYBOARD_NEXT_BAR_HEIGHT,
    minWidth: 64,
    padding: "0 14px",
    background: "transparent",
    border: "none",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 16,
    color: "#5050C8",
    cursor: "pointer",
  } as const;

  return createPortal(
    <div
      data-testid="keyboard-next-bar"
      ref={attachBar}
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom,
        height: KEYBOARD_NEXT_BAR_HEIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 4,
        padding: "0 6px",
        background: "#F0F0FF",
        borderTop: "1px solid #DCDCF4",
        // Above BottomSheet's overlay and every modal.
        zIndex: 2147483000,
      }}
    >
      {hasNext && (
        <button
          type="button"
          data-testid="keyboard-next"
          onClick={() => goToNextField(field)}
          style={btn}
        >
          Next
        </button>
      )}
      <button
        type="button"
        data-testid="keyboard-done"
        onClick={() => field.blur()}
        style={{ ...btn, fontWeight: 700 }}
      >
        Done
      </button>
    </div>,
    document.body,
  );
};

export default KeyboardNextBar;
